import { formatDecimalPlaces, formatDecimalPlacesNumerical, isValidNumber } from "./number";
/** Whole-number labour hours, grouped, never abbreviated. `1891980 → "1,891,980"`. */
export const hours = (value) => formatDecimalPlaces(value, 0);
/** Whole-number non-hour tallies — headcount, suppliers, transactions. `3789 → "3,789"`. */
export const count = (value) => formatDecimalPlaces(value, 0);
/**
 * Any percentage outside {@link percentPrecise}'s 2dp family. Whole numbers:
 * `11.4 → "11%"`. Percentage-point deltas add their own sign/`pp` at the call
 * site: `` `${d >= 0 ? "+" : ""}${count(d)}pp` ``.
 */
export const percent = (value) => `${formatDecimalPlaces(value, 0)}%`;
/**
 * A percentage someone typed, not measured — rounding it would restate the
 * commitment. Up to `maxDecimals` (default 2), trailing zeros trimmed:
 * `30.5 → "30.5%"`. Pass the precision the field that captured it accepts.
 */
export const percentAsEntered = (value, maxDecimals = 2) => `${Number(value.toFixed(maxDecimals))}%`;
/**
 * A percentage measured to 2dp, where {@link percent}'s rounding would
 * collapse rows the column exists to compare. `54.163 → "54.16%"`.
 */
export const percentPrecise = (value) => `${formatDecimalPlaces(value, 2)}%`;
/**
 * {@link percentPrecise}, except that a small non-zero value keeps enough
 * decimals to stay non-zero: `54.163 → "54.16%"`, but `0.0012 → "0.0012%"`.
 *
 * For a set of values spanning orders of magnitude — one line item's share of a
 * multi-million-dollar category, say — a fixed 2dp renders the whole small end
 * as "0.00%", which reads as "nothing reported" rather than "a small amount".
 * Two significant digits is the target; the cap keeps a near-zero value from
 * producing an unreadable string.
 *
 * Only for measured shares. A percentage someone TYPED should use
 * {@link percentAsEntered}, which must not gain decimals it wasn't given.
 * Where the whole-number form is wanted above 1%, use {@link percentShare} —
 * the same adaptive tail on a 0dp floor rather than a 2dp one.
 */
export const percentAdaptive = (value) => {
    const abs = Math.abs(value);
    if (abs === 0 || abs >= 0.01) {
        return percentPrecise(value);
    }
    const decimals = Math.min(8, Math.ceil(-Math.log10(abs)) + 1);
    return `${formatDecimalPlaces(value, decimals)}%`;
};
/**
 * A measured SHARE of a whole, spending decimals only where it must:
 * `91 → "91%"`, `0.48 → "0.48%"`, `0.0012 → "0.0012%"`.
 *
 * {@link percent} rounds a small share to `"0%"`, which reads as "none" — the
 * one thing a share that exists must not say. {@link percentPrecise} avoids
 * that by paying 2dp on every value, so a legend of whole numbers becomes
 * `"91.00%"` and the decimals carry no information. This keeps the whole-number
 * form wherever it is honest and drops to two significant digits below 1%,
 * capped so a near-zero value cannot produce an unreadable string.
 *
 * For chart legends and any other set of shares spanning orders of magnitude.
 * A percentage someone TYPED should use {@link percentAsEntered}, which must
 * not gain decimals it wasn't given; a column of shares being COMPARED at a
 * fixed precision wants {@link percentPrecise}.
 */
export const percentShare = (value) => {
    const abs = Math.abs(value);
    if (abs === 0 || abs >= 1) {
        return percent(value);
    }
    const decimals = Math.min(8, Math.ceil(-Math.log10(abs)) + 1);
    // `Number` trims the trailing zeros the fixed precision leaves behind, so
    // 0.6 reads "0.6%" and not "0.60%". Safe without grouping: it is below 1.
    return `${Number(value.toFixed(decimals))}%`;
};
/**
 * 4dp form of {@link percentPrecise} — a local-content-named exception, since
 * its only real usage is the local-content level-1 tables, where 2dp collapses
 * rows that differ in the third decimal. `54.16342 → "54.1634%"`.
 */
export const localContentPercentPrecise = (value) => `${formatDecimalPlaces(value, 4)}%`;
/** Emissions in tonnes CO2e, 2dp: `54.163 → "54.16"`. Unit affixed at call site. */
export const emissions = (value) => formatDecimalPlaces(value, 2);
/**
 * A measured value in its own unit — domain-free, 1dp, grouped:
 * `1234.56 → "1,234.6"`. Unit affixed at call site, like {@link emissions}.
 * Mostly environmental quantities today, but any tenth-precision value fits.
 */
export const quantity = (value) => formatDecimalPlaces(value, 1);
/** Magnitude thresholds for {@link moneyAbbreviated}, largest first. */
const MONEY_UNITS = [
    [1e9, "b"],
    [1e6, "m"],
    [1e3, "k"]
];
/**
 * Below this, mantissa keeps 1dp (`$1.1m`); at/above, it drops as false
 * precision (`$182k`). `99.95` not `100`: catches values that round up.
 */
const MONEY_DROP_DECIMAL_AT = 99.95;
/**
 * Decompose money for display: `$1.1m`, `$18.3k`, `$182k`, `$182`, `-$500`.
 * Significant-figures precision, not fixed decimals.
 *
 * Self-contained rather than composing `formatValueWithUnit`, which has ~20
 * out-of-scope callers whose locked-in output this would silently rewrite.
 */
export const moneyParts = (dollars) => {
    const value = isValidNumber(dollars) ? dollars : 0;
    const sign = value < 0 ? "-" : "";
    const magnitude = Math.abs(value);
    // Rounded, not raw: $999.60 renders "1,000" at 0dp, so it belongs in "k".
    if (Math.round(magnitude) < 1000) {
        return { mantissa: magnitude, places: 0, sign, unit: "" };
    }
    let index = MONEY_UNITS.findIndex(([threshold]) => Math.round(magnitude) >= threshold);
    let [threshold, unit] = MONEY_UNITS[index];
    let mantissa = magnitude / threshold;
    let places = mantissa >= MONEY_DROP_DECIMAL_AT ? 0 : 1;
    // 999,999 is "$1.0m", not "$1000k".
    if (Number(mantissa.toFixed(places)) >= 1000 && index > 0) {
        index -= 1;
        [threshold, unit] = MONEY_UNITS[index];
        mantissa = magnitude / threshold;
        places = mantissa >= MONEY_DROP_DECIMAL_AT ? 0 : 1;
    }
    return { mantissa, places, sign, unit };
};
/** Money as a display string — see {@link moneyParts} for the policy. */
export const moneyAbbreviated = (dollars) => {
    const { mantissa, places, sign, unit } = moneyParts(dollars);
    return `${sign}$${formatDecimalPlaces(mantissa, places)}${unit}`;
};
/**
 * Money on screen, full digits, no abbreviation: `204500 → "$204,500"`. Whole
 * dollars by default; `cents: true` for `204500.4 → "$204,500.40"`.
 *
 * Vs {@link moneyAbbreviated}: aggregates/tiles abbreviate, per-transaction
 * rows and chart hovers stay exact (reconciling against a source document). A
 * file export always shows cents regardless of kind — see `EXCEL_NUMBER_FORMATS`.
 */
export const money = (dollars, cents = false) => {
    const value = isValidNumber(dollars) ? dollars : 0;
    // Sign outside the `$`, matching `moneyAbbreviated`: "-$500", never "$-500".
    return `${value < 0 ? "-" : ""}$${formatDecimalPlaces(Math.abs(value), cents ? 2 : 0)}`;
};
/** Format a raw value for display under the given {@link FormattingKind}. */
export const formatForKind = (kind, value) => {
    switch (kind) {
        case "hours":
            return hours(value);
        case "count":
            return count(value);
        case "percent":
            return percent(value);
        case "percentAsEntered":
            return percentAsEntered(value);
        case "percentShare":
            return percentShare(value);
        case "percentPrecise":
            return percentPrecise(value);
        case "localContentPercentPrecise":
            return localContentPercentPrecise(value);
        case "moneyAbbreviated":
            return moneyAbbreviated(value);
        case "money":
            return money(value);
        case "emissions":
            return emissions(value);
        case "quantity":
            return quantity(value);
    }
};
/**
 * Decimal places {@link FormattingKind} renders at, for count-up animations.
 * {@link percentAsEntered} is the one kind whose precision is a property of
 * the value, not the kind — hence the optional `value`.
 */
export const decimalPlacesForKind = (kind, value = 0) => {
    switch (kind) {
        case "percentAsEntered":
            // Measured, not re-derived: 30 → 0, 30.5 → 1, 30.25 → 2.
            return (String(Number(value.toFixed(2))).split(".")[1] ?? "").length;
        case "percentShare":
            // Also a property of the value: whole above 1%, two significant digits
            // below it. Read off the rendered string so the two cannot disagree.
            return (percentShare(value).replace("%", "").split(".")[1] ?? "").length;
        case "localContentPercentPrecise":
            return 4;
        case "percentPrecise":
        case "emissions":
            return 2;
        case "quantity":
            return 1;
        default:
            return 0;
    }
};
/**
 * Display string at an explicit precision, for a number with no named kind
 * above. `formatAtPrecision(1234.5, 1) → "1,234.5"`. Reach for a kind first.
 */
export const formatAtPrecision = (value, decimalPlaces) => formatDecimalPlaces(value, decimalPlaces);
/**
 * Rounded number (not a string) for a spreadsheet/CSV cell — a string there
 * would make the column un-summable. `roundForExport(14.89361, 2) → 14.89`.
 */
export const roundForExport = (value, decimalPlaces = 2) => formatDecimalPlacesNumerical(value, decimalPlaces);
